import { useContext } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { LineChart } from '@givebutter/primitives/chart';
import { maskDecimal, maskWholeNumber } from '@givebutter/util';
import { faArrowDown, faArrowUp, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchReportStats } from 'api/reports';
import { ReportContext } from 'assets/js/pages/reports/ReportContext';
import { FetchQueryParams, useFetchQueryParams } from 'assets/js/pages/reports/useFetchQueryParams';
import * as DropdownMenu from 'components/dropdown-menu';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentReport } from 'customHooks/useCurrentReport';
import useDebounce from 'customHooks/useDebounce';
import { useTableSchema } from 'customHooks/useTableSchema';
import { isoDateFromServer } from 'helpers/dates';
import { transformCurrencyToCompactNumberFormat } from 'helpers/formatting';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { twJoin } from 'tailwind-merge';

type XAxisFormat = 'hour' | 'day' | 'month' | 'year';

const ReportChart = () => {
    const { accountId } = useCurrentAccount();
    const { data: report } = useCurrentReport();
    const { chartType, chartColumn, setChartColumn, setChartType } = useContext(ReportContext);
    const {
        data: { columns, graph },
    } = useTableSchema(report.resource);
    const chartColumns = Object.keys(graph);
    const currencySymbol = useCurrencySymbol();
    const params = useFetchQueryParams();
    const {
        sortByDesc: _sortByDesc,
        sortBy: _sortBy,
        display_settings: _display_settings,
        ...restParams
    } = params ?? ({} as FetchQueryParams);
    const { columns: columnsParams, ...restNextParams } = restParams;
    const debouncedColumns = useDebounce(columnsParams);
    const nextParams = {
        ...restNextParams,
        columns: debouncedColumns,
    };

    const { isPending, data: stats } = useSuspenseQuery({
        queryKey: accountQueryKeys.reportStats(accountId, report.id, nextParams),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchReportStats(accountId, report.id, nextParams),
                !!chartColumn && !!params,
            ),
    });

    if (!stats || !chartColumn) {
        return null;
    }

    const formatValueByColumnType = (value: string | number) => {
        if (chartColumn.value_type === 'numeric') {
            if (chartColumn.format === 'currency') {
                return `${currencySymbol}${maskDecimal(value)}`;
            }
            return maskWholeNumber(value);
        }

        if (typeof value === 'number') {
            return maskWholeNumber(value);
        }

        return value;
    };

    const formatTickLabelByColumnType = (value: number) => {
        const nextPrefix = chartColumn.format === 'currency' ? currencySymbol : '';
        const nextMask = value >= 999 ? transformCurrencyToCompactNumberFormat : maskWholeNumber;

        return `${nextPrefix}${nextMask(value)}`;
    };

    const calculatePercentageChange = () => {
        if (!stats.previous_period) {
            return 0;
        }

        const lastPeriodDataCount = stats.previous_period.count;
        const currentPeriodDataCount = stats.current_period.count;
        const change = currentPeriodDataCount - lastPeriodDataCount;

        return lastPeriodDataCount !== 0 ? (change / lastPeriodDataCount) * 100 : 100;
    };

    const currentChartSchema = graph[chartColumn.name];
    const hasMultipleGraphTypes = currentChartSchema.length > 1;

    const percentageChange = calculatePercentageChange();
    const noChange = percentageChange === 0;

    const getXAxisFromDateTime = (dt: DateTime, format: XAxisFormat) => {
        switch (format) {
            case 'hour':
                return `${dt.weekdayShort} ${dt.toLocaleString(DateTime.TIME_SIMPLE)}`;
            case 'day':
                return dt.toLocaleString(DateTime.DATE_MED);
            case 'month':
            case 'year':
                return `${dt.monthShort} ${dt.year}`;
        }
    };

    const data = stats.current_period.data.map(([date, value], i) => {
        return {
            date: getXAxisFromDateTime(isoDateFromServer(date), stats.format),
            'Current period': parseFloat(value as string),
            'Previous period': stats.previous_period?.data
                ? parseFloat(stats.previous_period.data[i][1] as string)
                : 0,
        };
    });

    return (
        <Box className={twJoin(`flex flex-col gap-4 p-4`, isPending && 'is-loading')}>
            <div className={'flex flex-wrap justify-between gap-4'}>
                <div className={'flex flex-col gap-1'}>
                    <div className={'flex items-center gap-1'}>
                        <p className={'text-xl font-semibold'}>
                            {formatValueByColumnType(stats.current_period.count)} {stats.labels[1]}
                        </p>
                        {stats.previous_period && (
                            <Badge
                                size={'sm'}
                                color={
                                    noChange
                                        ? 'neutral'
                                        : percentageChange > 0
                                          ? 'success'
                                          : 'error'
                                }
                                className="max-h-5"
                            >
                                {!noChange && (
                                    <FontAwesomeIcon
                                        icon={percentageChange > 0 ? faArrowUp : faArrowDown}
                                    />
                                )}
                                {maskDecimal(percentageChange)}%
                            </Badge>
                        )}
                    </div>
                    {stats.previous_period && (
                        <p className={'text-xs text-gray-500'}>
                            vs {formatValueByColumnType(stats.previous_period.count)} last period
                        </p>
                    )}
                </div>
                <div className={'flex gap-4'}>
                    {hasMultipleGraphTypes && (
                        <div className={'flex h-fit items-center gap-2'}>
                            <DropdownMenu.Root>
                                <DropdownMenu.Trigger>
                                    <Badge className={'flex max-h-5 gap-1.5'} size={'sm'}>
                                        {capitalize(chartType)}
                                        <FontAwesomeIcon size={'xs'} icon={faChevronDown} />
                                    </Badge>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content align={'end'} sideOffset={6}>
                                    {currentChartSchema.map(({ type }) => {
                                        return (
                                            <DropdownMenu.Item
                                                key={type}
                                                onSelect={() => setChartType(type)}
                                            >
                                                {capitalize(type)}
                                            </DropdownMenu.Item>
                                        );
                                    })}
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                            <DropdownMenu.Root>
                                <DropdownMenu.EllipsisTrigger />
                                <DropdownMenu.Content align={'end'} sideOffset={4}>
                                    {chartColumns.map(col => {
                                        const colFromSchema = columns.find(c => c.name === col);

                                        if (!colFromSchema) {
                                            return null;
                                        }

                                        return (
                                            <DropdownMenu.Item
                                                key={colFromSchema.name}
                                                onSelect={() => setChartColumn(colFromSchema)}
                                            >
                                                {colFromSchema.label}
                                            </DropdownMenu.Item>
                                        );
                                    })}
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </div>
                    )}
                </div>
            </div>
            <LineChart.Root
                data={data}
                startEndOnly={true}
                enableLegendToggle={true}
                index={'date'}
                yAxisWidth={48}
                categories={['Current period', 'Previous period']}
                valueFormatter={value => formatValueByColumnType(value).toString()}
                tickFormatter={(_, rawValue) => formatTickLabelByColumnType(rawValue).toString()}
            >
                <LineChart.Legend className={'w-full justify-end'} />
                <LineChart.Content className={'h-64'}>
                    <LineChart.Tooltip />
                </LineChart.Content>
            </LineChart.Root>
        </Box>
    );
};

export { ReportChart };
